import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, X, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";
import ProductCard from "@/components/ProductCard";
import { api } from "@/lib/api";

export default function Shop() {
    const { t } = useTranslation();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search] = useSearchParams();
    const initialCategory = search.get("category") || "All";
    const initialQuery = search.get("q") || "";
    const [activeCat, setActiveCat] = useState(initialCategory);
    const [query, setQuery] = useState(initialQuery);

    useEffect(() => {
        Promise.all([api.get("/products"), api.get("/categories")])
            .then(([p, c]) => {
                setProducts(p.data);
                setCategories(["All", ...c.data]);
            })
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        const matched = products.filter((p) => {
            if (activeCat !== "All" && p.category !== activeCat) return false;
            if (!q) return true;
            const haystack = [
                p.name,
                p.slug,
                p.short_name,
                p.category,
                p.description,
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return haystack.includes(q);
        });
        // Pens are a distinct product format — always render them at the bottom of
        // the grid, grouped after every vial/solvent, so shoppers see the standard
        // vial catalog first.
        return matched.slice().sort((a, b) => {
            const aPen = a.category === "Pens & Devices" ? 1 : 0;
            const bPen = b.category === "Pens & Devices" ? 1 : 0;
            return aPen - bPen;
        });
    }, [products, activeCat, query]);

    return (
        <div data-testid="shop-page" className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 py-12 sm:py-16 lg:py-24">
            <div className="border-b border-neutral-200 pb-8 sm:pb-12 mb-8 sm:mb-12">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-3">
                    {t("shop.eyebrow")}
                </div>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-extrabold uppercase tracking-tighter">
                    {t("shop.title")}
                </h1>
                <p className="mt-6 text-neutral-600 max-w-2xl leading-relaxed text-sm sm:text-base">
                    {t("shop.intro")}
                </p>
            </div>

            {/* PRODUCT SEARCH */}
            <div className="mb-6">
                <label
                    htmlFor="product-search"
                    className="block text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-3"
                >
                    {t("shop.searchLabel")}
                </label>
                <div className="relative max-w-2xl">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500 pointer-events-none" />
                    <input
                        id="product-search"
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={t("shop.searchPlaceholder")}
                        className="w-full bg-transparent border border-neutral-200 focus:border-[#00aeef] focus:outline-none focus:ring-1 focus:ring-[#00aeef]/40 pl-11 pr-11 py-3.5 text-sm text-neutral-900 placeholder:text-neutral-500 rounded-lg transition-colors"
                        data-testid="product-search-input"
                        autoComplete="off"
                    />
                    {query && (
                        <button
                            type="button"
                            onClick={() => setQuery("")}
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-neutral-500 hover:text-black rounded-md"
                            aria-label={t("shop.clear")}
                            data-testid="product-search-clear"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-6" data-testid="category-filters">
                {categories.map((c) => (
                    <button
                        key={c}
                        onClick={() => setActiveCat(c)}
                        className={`px-4 py-2 text-xs uppercase tracking-[0.18em] font-semibold border transition-colors rounded-lg ${
                            activeCat === c
                                ? "bg-[#0089c8] text-white border-[#0089c8]"
                                : "border-neutral-200 text-neutral-600 hover:border-[#0089c8] hover:text-black"
                        }`}
                        data-testid={`filter-${c.toLowerCase().replace(/[^a-z]/g, "-")}`}
                    >
                        {c === "All" ? t("shop.filterAll") : c}
                    </button>
                ))}
            </div>

            <div className="text-xs text-neutral-500 mb-8" data-testid="results-count">
                {loading
                    ? "…"
                    : t("shop.resultsCount", {
                          count: filtered.length,
                          total: products.length,
                          queryPart: query ? t("shop.resultsQueryPart", { query }) : "",
                      })}
            </div>

            {loading ? (
                <div className="text-neutral-500 text-sm uppercase tracking-[0.22em]">{t("shop.loading")}</div>
            ) : filtered.length === 0 ? (
                <div
                    className="border border-neutral-200 rounded-2xl p-12 text-center"
                    data-testid="no-results"
                >
                    <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-2">
                        {t("shop.noMatchesTitle")}
                    </div>
                    <div className="text-neutral-600 mb-6">
                        {t("shop.noMatchesBody", {
                            query,
                            where: activeCat === "All" ? t("shop.noMatchesWhereAll") : activeCat,
                        })}
                    </div>
                    <button
                        onClick={() => {
                            setQuery("");
                            setActiveCat("All");
                        }}
                        className="inline-flex items-center gap-2 border border-neutral-300 hover:border-[#0089c8] hover:text-black rounded-lg px-5 py-2.5 text-xs uppercase tracking-[0.18em] font-semibold"
                        data-testid="reset-filters"
                    >
                        {t("shop.resetFilters")}
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                    {filtered.map((p) => (
                        <ProductCard key={p.id} product={p} />
                    ))}
                </div>
            )}

            {/* REQUEST A PEPTIDE CALLOUT */}
            <div
                className="mt-16 sm:mt-24 border border-[#00aeef]/20 bg-gradient-to-br from-[#00aeef]/[0.06] to-transparent rounded-2xl p-6 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center gap-6 sm:gap-8"
                data-testid="request-peptide-callout"
            >
                <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-xl border border-[#00aeef]/30 bg-[#00aeef]/[0.06] flex items-center justify-center">
                    <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-[#0077b6]" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-2">
                        {t("shop.requestTitle")}
                    </div>
                    <p className="text-neutral-700 leading-relaxed text-sm sm:text-base max-w-2xl">
                        {t("shop.requestBody")}
                    </p>
                </div>
                <Link
                    to="/contact?subject=peptide-request"
                    className="flex-shrink-0 inline-flex items-center gap-2 bg-[#0089c8] text-white hover:bg-[#0077b6] transition-colors rounded-lg px-6 py-3 text-xs uppercase tracking-[0.18em] font-bold"
                    data-testid="request-peptide-cta"
                >
                    {t("shop.requestCta")}
                </Link>
            </div>
        </div>
    );
}
